<template>
  <div class="line-emoji-picker">
    <button type="button" class="btn-open" @click="open = true">
      <i class="ri-emotion-line"></i>
      LINE表情符號
    </button>

    <Teleport to="body">
      <div v-if="open" class="lep-backdrop" @click.self="open = false">
        <div class="lep-modal" role="dialog" aria-modal="true" aria-labelledby="lep-title" @keydown.esc="open = false">
          <div class="lep-head">
            <span id="lep-title">LINE表情符號</span>
            <button type="button" class="lep-close" aria-label="關閉" @click="open = false">
              <i class="ri-close-line"></i>
            </button>
          </div>
          <p class="lep-hint">
            點圖後會在游標處插入 <code v-pre>{{$1}}</code>、<code v-pre>{{$2}}</code> 等佔位（與下方 JSON 的 <code>slot</code> 對應，可剪貼移動），並寫入 <code>emojis</code>；儲存時會換算為 LINE 的 <code>$</code> 與 <code>index</code>。清單見
            <a href="https://developers.line.biz/zh-hant/docs/messaging-api/emoji-list/" target="_blank" rel="noopener noreferrer">官方文件</a>。
          </p>
          <div class="lep-toolbar">
            <label class="lep-label" for="lep-product">表情套組</label>
            <select id="lep-product" v-model="selectedProductId" class="form-control lep-select">
              <option v-for="p in catalog.items" :key="p.productId" :value="p.productId">
                {{ p.label }}（共 {{ p.count }} 個）
              </option>
            </select>
          </div>
          <div class="lep-grid-wrap">
            <div class="lep-grid">
              <button
                v-for="num in emojiNumbers"
                :key="num"
                type="button"
                class="lep-cell"
                :title="emojiIdStr(num)"
                @click="choose(num)"
              >
                <img
                  :src="previewUrl(num)"
                  loading="lazy"
                  width="40"
                  height="40"
                  alt=""
                  decoding="async"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import catalog from '@/data/lineEmojiCatalog.json';

const open = ref(false);
const selectedProductId = ref(catalog.items[0]?.productId || '');

watch(open, (v) => {
  if (v) document.body.classList.add('lep-modal-open');
  else document.body.classList.remove('lep-modal-open');
});

onUnmounted(() => {
  document.body.classList.remove('lep-modal-open');
});

const selectedProduct = computed(() =>
  catalog.items.find((p) => p.productId === selectedProductId.value) || catalog.items[0]
);

const emojiNumbers = computed(() => {
  const n = selectedProduct.value?.count || 0;
  return Array.from({ length: n }, (_, i) => i + 1);
});

function emojiIdStr(num) {
  return String(num).padStart(3, '0');
}

function previewUrl(num) {
  const pid = selectedProduct.value?.productId;
  if (!pid) return '';
  const id = emojiIdStr(num);
  return `https://stickershop.line-scdn.net/sticonshop/v1/sticon/${pid}/android/${id}.png`;
}

const emit = defineEmits(['pick']);

function choose(num) {
  const productId = selectedProduct.value?.productId;
  if (!productId) return;
  emit('pick', { productId, emojiId: emojiIdStr(num) });
  open.value = false;
}
</script>

<style scoped>
.btn-open {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.btn-open:hover {
  border-color: #93c5fd;
  background: #f8fafc;
  color: #1d4ed8;
}

.lep-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10050;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.lep-modal {
  width: 100%;
  max-width: 420px;
  max-height: min(88vh, 640px);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.lep-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
  font-size: 15px;
  color: #111827;
}

.lep-close {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #6b7280;
  font-size: 20px;
  line-height: 1;
  border-radius: 4px;
}
.lep-close:hover {
  color: #111827;
  background: #f3f4f6;
}

.lep-hint {
  margin: 0;
  padding: 10px 16px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
  border-bottom: 1px solid #f3f4f6;
}
.lep-hint code {
  font-size: 11px;
  padding: 1px 4px;
  background: #f3f4f6;
  border-radius: 3px;
}

.lep-toolbar {
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
}

.lep-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.lep-select {
  font-size: 13px;
}

.lep-grid-wrap {
  flex: 1;
  min-height: 200px;
  overflow-y: auto;
  padding: 12px 16px 16px;
}

.lep-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(44px, 1fr));
  gap: 6px;
}

.lep-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s, transform 0.1s;
}
.lep-cell:hover {
  border-color: #3b82f6;
  background: #eff6ff;
  transform: scale(1.04);
}
.lep-cell img {
  display: block;
  object-fit: contain;
}
</style>

<style>
/* 避免背景捲動（Teleport 至 body，需非 scoped） */
body.lep-modal-open {
  overflow: hidden;
}
</style>
